import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const GeneralQuoteForm = ({ onQuoteCreated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    quoteName: '',
    quoteType: 'Other',
    status: 'In Progress',
    customerId: '',
    customCustomerName: '',
    customCustomerEmail: '',
  });
  const [useCustomCustomer, setUseCustomCustomer] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch customers from the customers table
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const { data, error } = await supabase
          .from('customers')
          .select('id, name');
        if (error) throw error;
        setCustomers(data || []);
      } catch (err) {
        console.error('Error fetching customers:', err);
        setError('Failed to fetch customers.');
      }
    };

    fetchCustomers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let filePath = null;
      if (file) {
        const fileName = `${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('general-quote-files')
          .upload(`quotes/${user.id}/${fileName}`, file);
        if (uploadError) throw uploadError;
        filePath = `quotes/${user.id}/${fileName}`;
      }

      const { error: insertError } = await supabase
        .from('general_quotes')
        .insert({
          quote_name: formData.quoteName,
          created_by: user.id,
          file_path: filePath,
          customer_id: useCustomCustomer ? null : (formData.customerId || null),
          custom_customer_name: useCustomCustomer ? formData.customCustomerName : null,
          custom_customer_email: useCustomCustomer ? formData.customCustomerEmail : null,
          status: formData.status,
          quote_type: formData.quoteType,
        });

      if (insertError) throw insertError;

      setFormData({
        quoteName: '',
        quoteType: 'Other',
        status: 'In Progress',
        customerId: '',
        customCustomerName: '',
        customCustomerEmail: '',
      });
      setFile(null);
      setUseCustomCustomer(false);
      if (onQuoteCreated) onQuoteCreated();
      alert('Quote created successfully!');
    } catch (err) {
      console.error('Error creating quote:', err);
      setError('Failed to create quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Create General Quote</h2>
      {error && <p className="text-red-600 mb-4">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Quote Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quote Name</label>
            <input
              type="text"
              name="quoteName"
              value={formData.quoteName}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
              required
            />
          </div>

          {/* Quote Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quote Type</label>
            <select
              name="quoteType"
              value={formData.quoteType}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
            >
              <option value="Fabrication">Fabrication</option>
              <option value="Machine">Machine</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
            >
              <option value="In Progress">In Progress</option>
              <option value="Sent">Sent</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
              <option value="Expired">Expired</option>
            </select>
          </div>

          {/* Customer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <div className="flex items-center space-x-4 mb-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="customerType"
                  checked={!useCustomCustomer}
                  onChange={() => setUseCustomCustomer(false)}
                  className="mr-2"
                />
                Select Existing Customer
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="customerType"
                  checked={useCustomCustomer}
                  onChange={() => setUseCustomCustomer(true)}
                  className="mr-2"
                />
                Enter Custom Customer
              </label>
            </div>
            {!useCustomCustomer ? (
              <select
                name="customerId"
                value={formData.customerId}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Customer Name</label>
                  <input
                    type="text"
                    name="customCustomerName"
                    value={formData.customCustomerName}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                    required={useCustomCustomer}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Customer Email</label>
                  <input
                    type="email"
                    name="customCustomerEmail"
                    value={formData.customCustomerEmail}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                    required={useCustomCustomer}
                  />
                </div>
              </div>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Quote File</label>
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8">
          <button
            type="submit"
            disabled={loading}
            className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition shadow-sm ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {loading ? 'Creating Quote...' : 'Create Quote'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GeneralQuoteForm;